import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { describeSource, isAddressable, instantToWallClock, UnsavedChangesGuard } from '@racedash/shared'
import type { RisSource, SessionType } from '@racedash/shared'
import {
  deleteEvent,
  deleteSession,
  updateEvent,
  useEvents,
  useSeries,
  useSessions,
} from '../data/catalog'
import type { Series, Session } from '../data/catalog'
import EventForm, { RisSourceFields } from '../components/EventForm'
import SessionDialog from '../components/SessionDialog'
import CapturedChannels from '../components/CapturedChannels'
import FuelSourceCard from '../components/FuelSourceCard'
import { useCapturedSessions } from '../data/captured'
import TimetableImport from '../components/TimetableImport'
import { sectionTitle } from '../lib/sections'

export default function EventDetail() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  // Where the reader came from, recorded by the list they clicked. An event is
  // reachable from the calendar and from either vendor page, so "back" has no
  // single right answer — except on a fresh load, where there is no journey to
  // return to and the whole catalogue is the safe destination.
  const location = useLocation()
  const back = (location.state as { from?: string } | null)?.from ?? '/'

  const { events } = useEvents()
  const { sessions, error: sessionsError } = useSessions(eventId)
  const { series, error: seriesError } = useSeries()
  const { sessions: captured } = useCapturedSessions(eventId)

  const event = events?.find((e) => e.id === eventId)
  const [source, setSource] = useState<RisSource | null>(null)
  const [imported, setImported] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Owned here rather than in the form, because the thing being guarded is a
  // navigation away from this route and only the route can see one coming.
  const [dirty, setDirty] = useState(false)

  if (!events) return <Text c="dimmed">Loading…</Text>
  if (!event) {
    return (
      <Stack gap="xs">
        <Title order={1} size="h2">
          Not found
        </Title>
        <Text c="dimmed">
          No event with that id.{' '}
          <Anchor component={Link} to="/">
            Back to the calendar
          </Anchor>
        </Text>
      </Stack>
    )
  }

  const current = source ?? (event.source as RisSource)
  const ready = isAddressable(event.source)

  return (
    <Stack gap="lg">
      <UnsavedChangesGuard when={dirty} />

      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1} size="h2">
            {event.name}
          </Title>
          <Text c="dimmed" size="sm">
            {event.trackId} · {event.timezone} · {describeSource(event.source)}
          </Text>
        </div>
        <Button component={Link} to={back} variant="default">
          Back to {sectionTitle(back)}
        </Button>
      </Group>

      {!ready && (
        <Alert color="yellow" title="No timing address">
          This event has no timing address, so capture cannot start for it. RIS JSON events never
          need one — capture sweeps the channels — so this is a TSL event with no event id, or a
          RIS XML event with no path.
        </Alert>
      )}

      <Paper withBorder p="lg">
        <Title order={2} size="h5" mb="md">
          Details
        </Title>
        <EventForm
          event={event}
          source={current}
          // The vendor block is the caller's state, so only the caller knows
          // whether it has been touched.
          sourceDirty={source !== null}
          // A scraped event's name, circuit and dates are re-derived nightly,
          // so they are shown rather than offered for editing.
          vendorOwned={event.source.vendor === 'tsl' ? 'TSL' : undefined}
          onRevert={() => setSource(null)}
          sourceFields={
            event.source.vendor === 'ris' ? (
              <RisSourceFields
                source={current}
                onChange={setSource}
                series={series}
                eventSeriesIds={[...new Set((sessions ?? []).map((x) => x.seriesId))]}
                captured={captured}
              />
            ) : (
              <Text c="dimmed" size="sm">
                TSL addressing comes from the diary scrape and is not edited here.
              </Text>
            )
          }
          onDirtyChange={setDirty}
          onSubmit={async (input) => {
            // The event-wide preferred channel no longer exists; a stored one
            // is dropped on the next save rather than lingering unread.
            const { primaryChannel: _unused, ...kept } = (current ?? event.source) as RisSource & { primaryChannel?: string }
            await updateEvent(event.id, { ...input, source: event.source.vendor === 'ris' ? kept : event.source })
            setSource(null)
          }}
        />
      </Paper>

      {event.source.vendor === 'ris' && <CapturedChannels eventId={event.id} />}

      <FuelSourceCard event={event} />

      <SessionEditor
        eventId={event.id}
        timezone={event.timezone}
        firstDay={
          event.startsAt?.toDate
            ? instantToWallClock(event.startsAt.toDate(), event.timezone).slice(0, 10)
            : ''
        }
        sessions={sessions}
        series={series}
        error={sessionsError ?? seriesError}
        onImported={setImported}
      />

      {imported !== null && (
        <Alert color="green" withCloseButton onClose={() => setImported(null)}>
          Added {imported} {imported === 1 ? 'session' : 'sessions'}.
        </Alert>
      )}

      <Paper withBorder p="lg" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Title order={2} size="h5" c="red" mb="xs">
          Delete
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          Removes the event and its sessions. Captured timing is unaffected — it lives in stream
          sessions keyed by what the stream said, not by this record, which is the point of
          identifying afterwards rather than routing at capture time.
        </Text>
        <Button color="red" variant="light" onClick={() => setConfirmDelete(true)}>
          Delete event
        </Button>
      </Paper>

      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this event?"
      >
        <Stack gap="md">
          <Text size="sm">
            <strong>{event.name}</strong> and its {sessions?.length ?? 0} session
            {sessions?.length === 1 ? '' : 's'} will be removed. This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                setDirty(false)
                void deleteEvent(event.id).then(() => navigate(back))
              }}
            >
              Delete event
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

/**
 * The weekend's timetable.
 *
 * Reading and editing are separated: the table is the timetable as it stands,
 * and every way of changing it opens a dialog. That keeps the page scannable —
 * a permanently-open entry form is noise on every visit that is not adding
 * something — and it means adding, importing and editing all end in an explicit
 * confirmation rather than in a field losing focus.
 */
function SessionEditor({
  eventId,
  timezone,
  firstDay,
  sessions,
  series,
  error,
  onImported,
}: {
  eventId: string
  timezone: string
  /** The event's first day, as YYYY-MM-DD in its own timezone. */
  firstDay: string
  sessions: Session[] | null
  series: Series[] | null
  /** A failed listener, so a broken query cannot masquerade as an empty list. */
  error: string | null
  onImported: (count: number) => void
}) {
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Session | null>(null)
  const [importing, setImporting] = useState(false)
  const [removing, setRemoving] = useState<Session | null>(null)

  const noSeries = series?.length === 0

  return (
    <Paper withBorder p="lg">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={2} size="h5">
            Sessions
          </Title>
          <Text c="dimmed" size="sm">
            A weekend hosts several series, so each session names its own. Times are entered in{' '}
            {timezone} and stored as absolute instants.
          </Text>
        </div>
        <Group gap="xs">
          <Button variant="default" onClick={() => setImporting(true)} disabled={!!error || noSeries}>
            Import
          </Button>
          <Button onClick={() => setAdding(true)} disabled={!!error || noSeries}>
            New session
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert color="red">
          Could not load the timetable: {error}. Sessions already added are safe — this is the list
          failing, not the data.
        </Alert>
      )}

      {!error && noSeries && (
        <Alert color="yellow">
          No series defined yet. Add one under{' '}
          <Anchor component={Link} to="/series">
            Series
          </Anchor>{' '}
          before adding sessions.
        </Alert>
      )}

      {!error && sessions && sessions.length > 0 && (
        <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Session</Table.Th>
              <Table.Th w={160}>Series</Table.Th>
              <Table.Th w={150}>Starts</Table.Th>
              <Table.Th w={90}>Length</Table.Th>
              <Table.Th w={140} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sessions.map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Badge variant="light" size="sm" color={typeColour(s.type)}>
                      {s.type}
                    </Badge>
                    <Text size="sm">{s.name}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {series?.find((x) => x.id === s.seriesId)?.name ?? s.seriesId}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {s.scheduledStart?.toDate
                      ? instantToWallClock(s.scheduledStart.toDate(), timezone).replace('T', ' ')
                      : ''}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace" c="dimmed">
                    {describeLength(s)}
                  </Text>
                </Table.Td>
                <Table.Td align="right">
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <Button variant="subtle" size="compact-sm" onClick={() => setEditing(s)}>
                      Edit
                    </Button>
                    <Button
                      variant="subtle"
                      color="red"
                      size="compact-sm"
                      onClick={() => setRemoving(s)}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {!error && !noSeries && sessions?.length === 0 && (
        <Text c="dimmed" size="sm">
          No sessions yet. Add them one at a time, or import the organiser's published timetable.
        </Text>
      )}

      {series && series.length > 0 && (
        <>
          <SessionDialog
            opened={adding}
            onClose={() => setAdding(false)}
            eventId={eventId}
            timezone={timezone}
            firstDay={firstDay}
            series={series}
          />
          <SessionDialog
            opened={editing !== null}
            onClose={() => setEditing(null)}
            eventId={eventId}
            timezone={timezone}
            firstDay={firstDay}
            series={series}
            session={editing ?? undefined}
          />
          <TimetableImport
            opened={importing}
            onClose={() => setImporting(false)}
            eventId={eventId}
            timezone={timezone}
            series={series}
            onImported={onImported}
          />
        </>
      )}

      <Modal opened={removing !== null} onClose={() => setRemoving(null)} title="Delete this session?">
        <Stack gap="md">
          <Text size="sm">
            <strong>{removing?.name}</strong> will be removed from the timetable. This cannot be
            undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                const id = removing?.id
                setRemoving(null)
                if (id) void deleteSession(id)
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}

function typeColour(type: SessionType): string {
  if (type === 'race') return 'racedash'
  if (type === 'qualifying') return 'yellow'
  return 'gray'
}

/** How long a session runs, derived from the two instants it is stored as. */
function describeLength(s: Session): string {
  const start = s.scheduledStart?.toDate?.()
  const end = s.scheduledEnd?.toDate?.()
  if (!start || !end) return ''
  const mins = Math.round((end.getTime() - start.getTime()) / 60_000)
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}`
}
